import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Robust scroll restoration using location.key for unique history entries.
 * Uses ResizeObserver to detect when page content has loaded and become scrollable.
 */
export default function ScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType();
  const prevKey = useRef(location.key);
  const isRestoring = useRef(false);
  const targetScroll = useRef(0);

  // Disable browser's native scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Save scroll position continuously (debounced)
  useEffect(() => {
    let timeout;
    const handleScroll = () => {
      if (isRestoring.current) return; // Don't save while restoring
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        sessionStorage.setItem(`scroll_${location.key}`, String(window.scrollY));
      }, 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.key]);

  // ResizeObserver: when page grows tall enough, restore scroll
  useEffect(() => {
    if (!isRestoring.current || targetScroll.current <= 0) return;

    const observer = new ResizeObserver(() => {
      if (isRestoring.current && document.body.scrollHeight >= targetScroll.current) {
        window.scrollTo(0, targetScroll.current);
        if (Math.abs(window.scrollY - targetScroll.current) < 50) {
          isRestoring.current = false;
          observer.disconnect();
        }
      }
    });

    observer.observe(document.body);

    // Safety timeout: stop trying after 5 seconds
    const timeout = setTimeout(() => {
      isRestoring.current = false;
      observer.disconnect();
    }, 5000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [location.key, navType]);

  useEffect(() => {
    // Save scroll before navigating away
    if (prevKey.current !== location.key) {
      sessionStorage.setItem(`scroll_${prevKey.current}`, String(window.scrollY));
      prevKey.current = location.key;
    }

    if (navType === 'POP') {
      const saved = parseInt(sessionStorage.getItem(`scroll_${location.key}`) || '0', 10);
      if (saved > 0) {
        targetScroll.current = saved;
        isRestoring.current = true;
        // Try immediately in case page is already tall enough
        window.scrollTo(0, saved);
        if (Math.abs(window.scrollY - saved) < 50) {
          isRestoring.current = false;
        }
      }
    } else {
      // PUSH/REPLACE — scroll to top
      isRestoring.current = false;
      targetScroll.current = 0;
      window.scrollTo(0, 0);
    }
  }, [location.key, navType]);

  return null;
}
